<template>
  <div class="musubi-page dm-page">
    <header class="musubi-page__head">
      <div class="dm-head-row">
        <h1>Direct Mail - Design your mail piece <span class="dm-badge">New</span></h1>
        <span v-if="loaded && !isPaying" class="plan-pill plan-pill--free"><CygnusIcon name="info-circle" /> Free plan</span>
      </div>
      <p class="text-muted">
        Pick a format, then either <strong>design it in Yorlin</strong> with merge tokens pulled from each recipient's
        corpus record, or <strong>upload your own print-ready PDF</strong>.
        <template v-if="loaded && !isPaying">
          The design tools are all yours on the free plan - only the <strong>AI copy assists</strong> need a plan.
        </template>
      </p>
    </header>

    <!-- Stepper -->
    <nav class="dm-steps" aria-label="Direct mail steps">
      <div class="dm-step is-done">
        <span class="dm-step__num"><CygnusIcon name="check" /></span>
        <span class="dm-step__body"><span class="dm-step__title">Who gets mail</span><span class="dm-step__sub">Audience</span></span>
      </div>
      <div class="dm-step is-current">
        <span class="dm-step__num">2</span>
        <span class="dm-step__body"><span class="dm-step__title">Design your mail piece</span><span class="dm-step__sub">Format &amp; artwork</span></span>
      </div>
      <div class="dm-step is-todo">
        <span class="dm-step__num">3</span>
        <span class="dm-step__body"><span class="dm-step__title">{{ isPaying ? 'Review &amp; submit' : 'Buy this campaign' }}</span><span class="dm-step__sub">{{ isPaying ? 'Confirm' : 'One-off checkout' }}</span></span>
      </div>
    </nav>

    <div v-if="error" class="dm-alert dm-alert--err" style="margin-bottom:16px">{{ error }}</div>

    <!-- ── Format picker ── -->
    <section class="dm-card">
      <div class="dm-card__head"><CygnusIcon name="envelope-open-text" class="dm-card__icon" /><h3 class="dm-card__title">Choose a format</h3><span class="dm-card__subtitle">physical size and indicative print cost</span></div>
      <div class="dm-card__body">
        <div class="fmt-grid">
          <button
            v-for="f in formats" :key="f.key" type="button"
            class="fmt-card" :class="{ 'is-on': design.format === f.key }"
            @click="setFormat(f.key)"
          >
            <div class="fmt-card__vis"><CygnusIcon :name="f.double_sided ? 'credit-card' : 'file'" /></div>
            <div class="fmt-card__name">{{ f.label }}</div>
            <div class="fmt-card__dim">{{ f.dims_label }}</div>
            <div class="fmt-card__cost">{{ f.from_label }}</div>
          </button>
        </div>
      </div>
    </section>

    <!-- ── Two creation paths ── -->
    <div class="dm-paths">
      <!-- (a) Design in Yorlin -->
      <section class="dm-card">
        <div class="dm-card__body">
          <div class="path-head"><CygnusIcon name="edit" /><h4>Design in Yorlin</h4></div>
          <p class="path-sub">A simple block editor. Merge tokens are substituted per recipient from their corpus record.<template v-if="!isPaying"> Editing is free - the AI writer needs a plan.</template></p>

          <div class="editor">
            <div class="toolbar">
              <span class="tool"><CygnusIcon name="pen" /> Text</span>
              <span class="tool"><CygnusIcon name="palette" /> Image</span>
              <!-- Front / Back side switch. Two pills carrying the same `is-on`
                   active treatment the format cards and QR-size pills use, so
                   the current side is always visible (a single combined
                   "Front / Back" label showed no active state at all). -->
              <span v-if="isDoubleSided" class="tool tool--sides" role="group" aria-label="Mail piece side">
                <CygnusIcon name="credit-card" />
                <button
                  v-for="s in sides" :key="s.key" type="button"
                  class="side-pill" :class="{ 'is-on': side === s.key }"
                  :aria-pressed="side === s.key ? 'true' : 'false'"
                  @click="side = s.key"
                >{{ s.label }}</button>
              </span>
              <!-- AI assist: locked (free) or coming-soon (paying) -->
              <span class="lock-wrap">
                <button
                  type="button" class="tool tool--ai" :disabled="true"
                  :title="isPaying ? 'AI copy generation is coming soon' : 'AI copy in your voice - available on the Growth plan'"
                >
                  <CygnusIcon name="bolt" /> Write it for me
                  <CygnusIcon v-if="!isPaying" name="lock" class="tool__lock" />
                  <span v-else class="tool__soon">soon</span>
                </button>
              </span>
            </div>

            <!-- Canvas (front/back) -->
            <div v-if="side === 'front' || !isDoubleSided" class="canvas" :style="canvasStyle">
              <span class="canvas__side">{{ isDoubleSided ? 'Front - preview' : 'Preview' }}</span>
              <div class="blk-edit" @focusin="onEditorFocusIn">
                <input class="blk-input blk-input--h" v-model="front.headline" placeholder="Headline" data-token-field="headline" @input="scheduleSave" />
                <textarea ref="bodyInput" class="blk-input blk-input--p" v-model="front.body" rows="3" placeholder="Body copy - drop tokens in here" data-token-field="body" @input="scheduleSave"></textarea>
                <input class="blk-input blk-input--cta" v-model="front.cta" placeholder="Call to action" data-token-field="cta" @input="scheduleSave" />
                <label class="blk-img-row"><CygnusIcon name="palette" /> <input class="blk-input blk-input--img" v-model="front.imageLabel" placeholder="Image placeholder label" @input="scheduleSave" /></label>

                <!-- QR block -->
                <div class="blk-qr-row">
                  <label class="qr-toggle">
                    <input type="checkbox" v-model="front.qrEnabled" @change="scheduleSave" />
                    <CygnusIcon name="mobile" /> Add a QR code
                  </label>
                  <template v-if="front.qrEnabled">
                    <div class="qr-controls">
                      <div class="qr-sizes">
                        <button
                          v-for="s in qrSizes" :key="s.key" type="button"
                          class="qr-size" :class="{ 'is-on': front.qrSize === s.key }"
                          @click="setQrSize(s.key)"
                        >{{ s.label }}</button>
                      </div>
                      <label class="qr-cap-toggle">
                        <input type="checkbox" v-model="front.qrCaption" @change="scheduleSave" /> Show the code URL underneath
                      </label>
                    </div>
                    <div class="qr-sample">
                      <div class="qr-glyph" aria-hidden="true"><CygnusIcon name="mobile" /></div>
                      <div class="qr-sample-txt">
                        <b>Each recipient gets their own unique code - scans are tracked.</b>
                        The real QR renders on the proof and on every printed piece.
                      </div>
                    </div>
                  </template>
                </div>
              </div>

              <!-- Address block appears on the front for single-sided formats -->
              <div v-if="!isDoubleSided" class="blk blk-addr">
                <div v-for="(line, i) in previewAddressLines" :key="i">{{ line }}</div>
              </div>

              <!-- Live preview -->
              <div class="live-preview">
                <div class="live-preview__lbl">Live preview - sample record</div>
                <div class="lp-h" v-if="mergedHeadline">{{ mergedHeadline }}</div>
                <div class="lp-p" v-if="mergedBody">{{ mergedBody }}</div>
                <div class="lp-cta" v-if="front.cta">{{ front.cta }}</div>
              </div>
            </div>

            <div v-else class="canvas canvas--back">
              <span class="canvas__side">Back - address side</span>
              <div class="blk blk-addr">
                <div v-for="(line, i) in previewAddressLines" :key="i">{{ line }}</div>
              </div>
              <span class="f-note" style="margin-top:10px">The back always carries the address block - it is how the piece is posted.</span>
            </div>

            <div class="f-row">
              <label class="f-lbl">Merge tokens - click to insert at your cursor</label>
              <div class="token-palette">
                <button
                  v-for="t in tokens" :key="t" type="button"
                  class="token" :class="{ 'is-known': knownTokens.includes(t) }"
                  @click="insertToken(t)"
                >{{ t }}</button>
              </div>
              <span class="f-note"><CygnusIcon name="info-circle" /> Teal tokens (like <code>{{ firstNameToken }}</code>) only fill where a named contact exists - blank and safe otherwise.</span>
            </div>

            <div class="info-state">
              <CygnusIcon name="bolt" />
              <span><b>Live preview</b> is substituting a sample record: {{ sampleLabel }}. No first name on file, so <code>{{ firstNameToken }}</code> is dropped from the greeting.</span>
            </div>

            <!-- AI upsell strip (free only, violet plan-gate) -->
            <div v-if="loaded && !isPaying" class="info-state info-state--violet">
              <CygnusIcon name="lock" />
              <span><b>AI copy assists are a plan feature.</b> On the Growth plan, "Write it for me" drafts headline, body and call-to-action in your brand voice, and rewrites for tone and length.
                <router-link to="/admin/usage-plans" class="violet-link">See plans</router-link></span>
            </div>
          </div>
        </div>
      </section>

      <!-- (b) Upload PDF -->
      <section class="dm-card">
        <div class="dm-card__body">
          <div class="path-head"><CygnusIcon name="upload" /><h4>Upload your own PDF</h4></div>
          <p class="path-sub">Already have artwork from a designer? Drop the print-ready PDF and we will scan it. This path is fully available on every plan.</p>

          <label class="dropzone" :class="{ 'is-busy': uploading }">
            <input type="file" accept="application/pdf,.pdf" class="dropzone__input" @change="onFilePicked" :disabled="uploading" />
            <div class="dropzone__ic"><CygnusIcon name="upload" /></div>
            <div v-if="uploading"><b>Scanning {{ uploadName }}…</b></div>
            <div v-else-if="upload.report && upload.report.is_pdf"><b>{{ uploadName }}</b> {{ upload.accepted ? 'uploaded' : 'checked' }}</div>
            <div v-else><b>Drop a print-ready PDF</b> or click to choose</div>
            <div class="hint">PDF up to 20 MB - front / back as separate pages for postcards</div>
          </label>

          <div v-if="uploadError" class="dm-alert dm-alert--err" style="margin:12px 0 0">{{ uploadError }}</div>

          <!-- Validation report -->
          <template v-if="upload.report">
            <div class="scan" v-if="upload.accepted">
              <div class="scan__head"><CygnusIcon name="check" /> Address block will be added for you</div>
              <div class="scan__body">
                <div v-for="t in addressBlockTokens" :key="t" class="scan-token"><span class="tkn">{{ t }}</span><span class="pg">address block</span></div>
              </div>
            </div>

            <div class="validation">
              <span class="vchip" :class="upload.report.size_match === 'ok' ? 'ok' : 'warn'">
                <CygnusIcon :name="upload.report.size_match === 'ok' ? 'check' : 'warning'" />
                {{ upload.report.size_match === 'ok' ? 'Page size matches ' + upload.report.size_expected : 'Page size differs from ' + upload.report.size_expected }}
              </span>
              <span class="vchip" :class="upload.report.encrypted ? 'warn' : 'ok'">
                <CygnusIcon :name="upload.report.encrypted ? 'warning' : 'check'" />
                {{ upload.report.encrypted ? 'Encrypted - not accepted' : 'Not encrypted' }}
              </span>
              <span class="vchip note">{{ upload.report.page_count }} {{ upload.report.page_count === 1 ? 'page' : 'pages' }}</span>
            </div>

            <div class="info-state" style="margin-top:12px">
              <CygnusIcon name="info-circle" />
              <span><b>Uploaded PDFs are personalised in the address block only.</b> We position the name and address for you; the rest of the artwork prints exactly as supplied. Want tokens anywhere on the page? Design it in Yorlin instead.</span>
            </div>

            <!-- Address-block position picker (after an accepted upload) -->
            <div v-if="upload.accepted && upload.presets.length" class="f-row" style="margin-top:14px">
              <label class="f-lbl">Address block position</label>
              <div class="chip-row">
                <button
                  v-for="p in upload.presets" :key="p.key" type="button"
                  class="chip" :class="{ 'is-on': overlayPosition === p.key }"
                  @click="setOverlayPosition(p.key)"
                >{{ p.label }}</button>
              </div>
            </div>
          </template>
        </div>
      </section>
    </div>

    <!-- ── Proof + continue ── -->
    <section class="dm-card">
      <div class="dm-card__head"><CygnusIcon name="file" class="dm-card__icon" /><h3 class="dm-card__title">Print-ready output</h3><span class="dm-card__subtitle">proof preview with a sample record merged</span></div>
      <div class="dm-card__body">
        <div class="dm-action-bar" style="margin-top:0">
          <span class="dm-action-bar__note">
            Proof shown with {{ sampleLabel }} merged. The final PDF is generated per recipient at submission.
          </span>
          <span class="dm-action-bar__spacer"></span>
          <router-link :to="backLink" class="btn btn-sec"><CygnusIcon name="arrow-left" /> Back</router-link>
          <button class="btn btn-sec" :disabled="!design.id || generating || design.source === 'upload'" :title="design.source === 'upload' ? 'Uploaded PDFs print as supplied' : 'Open a print-ready proof'" @click="generateProof">
            <CygnusIcon name="file" /> {{ generating ? 'Generating…' : 'Generate proof' }}
          </button>
          <button class="btn btn-pri" :disabled="!design.id || continuing" :title="isPaying ? 'Review your mailing before submitting' : 'Review your mailing'" @click="continueToReview">{{ continuing ? 'Preparing…' : 'Continue to review' }} <CygnusIcon name="arrow-right" /></button>
        </div>
      </div>
    </section>

    <transition name="dm-toast">
      <div v-if="message" class="dm-toast"><CygnusIcon name="check-circle" /> {{ message }}</div>
    </transition>
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";

/**
 * DirectMailBuilder — "Design your mail piece" (Direct Mail slice DM-2).
 *
 * Faithful implementation of mockups/direct-mail/builder.html + builder-free.html:
 * a format picker, the two-path layout (Yorlin block editor with a token palette
 * and live sample-record substitution + postcard front/back tabs, OR upload-your-
 * own-PDF with a validation report and an address-block position picker), and a
 * "Generate proof" action that opens the server-rendered proof.pdf.
 *
 * A draft design is created on mount and autosaved (debounced PATCH). The AI copy
 * assists are SHOWN but disabled: violet plan-gate for non-paying tenants, an
 * enabled-looking "coming soon" for paying ones (AI copy generation is a later
 * slice - not built here). Copy uses plain hyphens (never em dashes).
 */
export default {
  name: "DirectMailBuilder",
  props: { componentData: { type: Object, default: () => ({}) } },
  data() {
    return {
      api: useApi(),
      loaded: false,
      error: null,
      message: "",
      messageTimer: null,
      saveTimer: null,
      generating: false,
      continuing: false,
      isPaying: true,
      tokens: [],
      knownTokens: ["{{first_name}}"],
      formats: [],
      side: "front",
      sides: [
        { key: "front", label: "Front" },
        { key: "back", label: "Back" },
      ],
      activeField: "body",
      // The text field the caret was last in - the merge-token palette inserts
      // there. Not reactive state the template reads, but kept on `data` so it
      // is reset with the component.
      lastFocusedEl: null,
      firstNameToken: "{{first_name}}",
      design: {
        id: null,
        format: "postcard_a6",
        source: "builder",
        audience_id: null,
      },
      front: {
        headline: "Fancy a fresh look this spring?",
        body: "Hi {{first_name}}, we help independent businesses like {{company_name}} get found online. Pop in for a chat.",
        cta: "Book a free consult",
        imageLabel: "Your image or logo",
        qrEnabled: false,
        qrSize: "m",
        qrCaption: true,
      },
      qrSizes: [
        { key: "s", label: "Small" },
        { key: "m", label: "Medium" },
        { key: "l", label: "Large" },
      ],
      sample: {
        "{{company_name}}": "Cockermouth Travel",
        "{{address_line_1}}": "60 Main Street",
        "{{address_line_2}}": "",
        "{{town}}": "Cockermouth",
        "{{postcode}}": "CA13 9LU",
        "{{first_name}}": "",
      },
      upload: { report: null, accepted: false, presets: [] },
      // Mirrors DesignUploadService::MAX_BYTES (20 MB) so the FE can reject an
      // oversize file with a reason before spending the round trip.
      maxUploadBytes: 20 * 1024 * 1024,
      uploadName: "",
      uploading: false,
      uploadError: null,
      overlayPosition: null,
      addressBlockTokens: ["{{company_name}}", "{{address_line_1}}", "{{town}}", "{{postcode}}"],
    };
  },
  computed: {
    isDoubleSided() {
      const f = this.formats.find((x) => x.key === this.design.format);
      return f ? !!f.double_sided : true;
    },
    canvasStyle() {
      const f = this.formats.find((x) => x.key === this.design.format);
      // aspect from the dims label is not reliable; keep a sensible postcard ratio.
      return this.isDoubleSided ? { aspectRatio: "148 / 105" } : { aspectRatio: "210 / 297" };
    },
    mergedHeadline() {
      return this.mergePreview(this.front.headline);
    },
    mergedBody() {
      return this.mergePreview(this.front.body);
    },
    previewAddressLines() {
      const order = ["{{company_name}}", "{{address_line_1}}", "{{address_line_2}}", "{{town}}", "{{postcode}}"];
      return order.map((t) => (this.sample[t] || "").trim()).filter((v) => v !== "");
    },
    sampleLabel() {
      const parts = [this.sample["{{company_name}}"], this.sample["{{address_line_1}}"], this.sample["{{town}}"], this.sample["{{postcode}}"]]
        .map((v) => (v || "").trim())
        .filter((v) => v !== "");
      return parts.join(", ");
    },
    backLink() {
      return this.design.audience_id
        ? { path: "/outreach/direct-mail", query: { audience: this.design.audience_id } }
        : { path: "/outreach/direct-mail" };
    },
  },
  created() {
    const q = this.$route && this.$route.query ? this.$route.query : {};
    const aud = Number(q.audience);
    if (aud > 0) this.design.audience_id = aud;
    this.boot();
  },
  beforeUnmount() {
    if (this.saveTimer) clearTimeout(this.saveTimer);
    if (this.messageTimer) clearTimeout(this.messageTimer);
  },
  methods: {
    async boot() {
      await this.loadTier();
      await this.createDraft();
      await this.loadSample();
      this.loaded = true;
    },
    async loadTier() {
      try {
        const res = await this.api.get("/api/v1/direct-mail/tier");
        const body = res?.data ?? res;
        this.isPaying = !!body?.is_paying;
        if (Array.isArray(body?.tokens)) this.tokens = body.tokens;
        if (Array.isArray(body?.known_tokens)) this.knownTokens = body.known_tokens;
        if (Array.isArray(body?.formats)) this.formats = body.formats;
      } catch (e) {
        this.tokens = ["{{company_name}}", "{{address_line_1}}", "{{address_line_2}}", "{{town}}", "{{postcode}}", "{{first_name}}"];
      }
    },
    async createDraft() {
      try {
        const res = await this.api.post("/api/v1/direct-mail/designs", {
          name: "Untitled design",
          format: this.design.format,
          source: "builder",
          audience_id: this.design.audience_id,
          blocks: this.blocksPayload(),
        });
        const body = res?.data ?? res;
        const d = body?.design;
        if (d && d.id) {
          this.design.id = d.id;
          this.design.format = d.format || this.design.format;
          this.design.audience_id = d.audience_id ?? this.design.audience_id;
        }
      } catch (e) {
        this.error = this.errText(e, "Could not start a new design.");
      }
    },
    async loadSample() {
      // Pull the first row of the linked audience as the live-preview sample.
      if (!this.design.audience_id) return;
      try {
        const a = await this.api.get("/api/v1/direct-mail/audiences/" + this.design.audience_id);
        const filters = (a?.data ?? a)?.audience?.filters || {};
        const res = await this.api.post("/api/v1/direct-mail/audience/preview", { filters, page: 1, per_page: 1 });
        const rows = (res?.data ?? res)?.rows || [];
        if (rows.length) this.applySample(rows[0]);
      } catch (e) {
        /* sample is a nicety - the placeholder still previews fine */
      }
    },
    applySample(row) {
      const [l1, l2, town, postcode] = this.splitAddress(String(row.address || ""));
      this.sample = {
        "{{company_name}}": String(row.business || row.name || ""),
        "{{address_line_1}}": l1,
        "{{address_line_2}}": l2,
        "{{town}}": town,
        "{{postcode}}": postcode,
        "{{first_name}}": "",
      };
    },
    // Mirror of the backend RecordTokens address split (postcode-first, comma parts).
    splitAddress(address) {
      address = (address || "").trim();
      if (!address) return ["", "", "", ""];
      const pc = /[A-Z]{1,2}[0-9][0-9A-Z]?\s*[0-9][A-Z]{2}/gi;
      let postcode = "";
      const matches = address.match(pc);
      if (matches && matches.length) {
        postcode = matches[matches.length - 1].toUpperCase().replace(/\s+/g, " ").trim();
        address = address.replace(pc, "").trim();
      }
      const parts = address.split(",").map((p) => p.replace(/^[\s,]+|[\s,]+$/g, "")).filter((p) => p !== "");
      let l1 = parts[0] || "", l2 = "", town = "";
      if (parts.length >= 3) {
        town = parts[parts.length - 1];
        l2 = parts.slice(1, parts.length - 1).join(", ");
      } else if (parts.length === 2) {
        town = parts[1];
      }
      return [l1, l2, town, postcode];
    },
    mergePreview(text) {
      return String(text || "")
        .replace(/\{\{\s*([a-z0-9_]+)\s*\}\}/gi, (_, k) => this.sample["{{" + k.toLowerCase() + "}}"] || "")
        .replace(/[ \t]{2,}/g, " ")
        .replace(/\s+([,.!?])/g, "$1")
        .replace(/,\s*,/g, ",")
        .trim();
    },
    setFormat(key) {
      this.design.format = key;
      if (!this.isDoubleSided) this.side = "front";
      this.scheduleSave();
    },
    setQrSize(key) {
      this.front.qrSize = key;
      this.scheduleSave();
    },
    /**
     * Remember which text field the caret was last in, so a token click can
     * land there. Clicking a palette button blurs the field, but the browser
     * keeps selectionStart/selectionEnd on the element after blur - that is
     * what makes caret insertion work from an out-of-field button.
     */
    onEditorFocusIn(ev) {
      const el = ev.target;
      const field = el && el.dataset ? el.dataset.tokenField : null;
      if (!field || !(field in this.front)) return;
      this.activeField = field;
      this.lastFocusedEl = el;
    },
    /**
     * Insert a merge token AT THE CARET of the last-focused text field (replacing
     * any selection), not at the end of the body. Appending was the reported bug:
     * repeated clicks stacked tokens on the tail of the field instead of dropping
     * them where the user was typing.
     */
    insertToken(t) {
      const el = this.lastFocusedEl && this.lastFocusedEl.isConnected ? this.lastFocusedEl : this.$refs.bodyInput;
      const field = el && el.dataset && el.dataset.tokenField && el.dataset.tokenField in this.front
        ? el.dataset.tokenField
        : "body";
      const current = this.front[field] || "";

      // No live element (side flipped to back, ref not mounted) - fall back to
      // appending so the click is never a no-op.
      if (!el || typeof el.selectionStart !== "number" || typeof el.selectionEnd !== "number") {
        this.front[field] = (current ? current + " " : "") + t;
        this.scheduleSave();
        return;
      }

      const start = el.selectionStart;
      const end = el.selectionEnd;
      this.front[field] = current.slice(0, start) + t + current.slice(end);
      this.activeField = field;
      this.lastFocusedEl = el;

      // Restore the caret AFTER the inserted token once Vue has flushed the
      // v-model write back into the DOM, so consecutive clicks keep advancing.
      const caret = start + t.length;
      this.$nextTick(() => {
        if (!el.isConnected) return;
        el.focus();
        try { el.setSelectionRange(caret, caret); } catch (e) { /* non-text input - ignore */ }
      });

      this.scheduleSave();
    },
    blocksPayload() {
      const front = [];
      if (this.front.headline) front.push({ type: "headline", text: this.front.headline });
      if (this.front.body) front.push({ type: "body", text: this.front.body });
      if (this.front.imageLabel) front.push({ type: "image", label: this.front.imageLabel });
      if (this.front.cta) front.push({ type: "cta", text: this.front.cta });
      if (this.front.qrEnabled) front.push({ type: "qr", size: this.front.qrSize, caption: this.front.qrCaption });
      return { front, back: [{ type: "address" }], colors: { accent: "#4F46E5" } };
    },
    scheduleSave() {
      if (this.saveTimer) clearTimeout(this.saveTimer);
      this.saveTimer = setTimeout(() => this.save(), 500);
    },
    async save() {
      if (!this.design.id) return;
      try {
        await this.api.patch("/api/v1/direct-mail/designs/" + this.design.id, {
          format: this.design.format,
          blocks: this.blocksPayload(),
        });
      } catch (e) {
        /* autosave is best-effort; a hard failure surfaces on proof */
      }
    },
    async generateProof() {
      if (!this.design.id) return;
      this.generating = true;
      try {
        await this.save();
        window.open("/api/v1/direct-mail/designs/" + this.design.id + "/proof.pdf", "_blank");
      } finally {
        this.generating = false;
      }
    },
    async continueToReview() {
      if (!this.design.id) return;
      this.continuing = true;
      try {
        // Persist the latest edits, then mark the design ready so the review step
        // can build a mailing from it.
        await this.save();
        await this.api.patch("/api/v1/direct-mail/designs/" + this.design.id, { status: "ready" });
        const query = { design: this.design.id };
        if (this.design.audience_id) query.audience = this.design.audience_id;
        this.$router.push({ path: "/outreach/direct-mail/review", query });
      } catch (e) {
        this.error = this.errText(e, "Could not continue to the review step.");
        this.continuing = false;
      }
    },
    async onFilePicked(ev) {
      const file = ev.target.files && ev.target.files[0];
      if (!file || !this.design.id) return;
      this.uploadName = file.name;
      this.uploadError = null;

      // Say WHY up front for the two things we can check without a round trip.
      const looksPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name || "");
      if (!looksPdf) {
        this.upload = { report: null, accepted: false, presets: [] };
        this.uploadError = "That file is not a PDF (" + (file.type || "unknown type") + "). Upload a print-ready PDF.";
        ev.target.value = "";
        return;
      }
      if (file.size > this.maxUploadBytes) {
        this.upload = { report: null, accepted: false, presets: [] };
        this.uploadError = "That PDF is " + this.mb(file.size) + " - over the " + this.mb(this.maxUploadBytes)
          + " limit. Flatten or downsample the artwork and try again.";
        ev.target.value = "";
        return;
      }

      this.uploading = true;
      try {
        const fd = new FormData();
        fd.append("file", file);
        if (this.overlayPosition) fd.append("position", this.overlayPosition);
        /*
         * The Content-Type header MUST be set here. The shared icarus api client
         * builds its axios instance with a default `Content-Type: application/json`,
         * and axios's transformRequest turns FormData into `JSON.stringify(
         * formDataToJSON(fd))` whenever the content type is JSON - a File
         * serialises to `{}`, so the request went out as `{"file":{}}` and the
         * server correctly answered "No file was uploaded". Declaring multipart
         * keeps the FormData intact; the browser then replaces this header with
         * the real one including the boundary (axios resolveConfig unsets it for
         * FormData). This was the "no PDF ever uploads" bug.
         */
        const res = await this.api.post("/api/v1/direct-mail/designs/" + this.design.id + "/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const body = res?.data ?? res;
        this.upload = {
          report: body?.report || null,
          accepted: !!body?.accepted,
          presets: Array.isArray(body?.presets) ? body.presets : [],
        };
        if (body?.token_plan?.position) this.overlayPosition = body.token_plan.position;
        if (body?.design) {
          this.design.source = body.design.source || "upload";
        }
        this.toast("PDF checked.");
      } catch (e) {
        const body = e?.response?.data ?? e?.data;
        this.upload = { report: body?.report || null, accepted: false, presets: [] };
        this.uploadError = this.uploadErrText(e);
      } finally {
        this.uploading = false;
        ev.target.value = "";
      }
    },
    /**
     * Turn an upload failure into a reason the user can act on. The validator
     * already returns a specific `reason` (not a PDF / over 20 MB / encrypted /
     * unreadable) - surface it verbatim; otherwise name the class of failure
     * (too large, rejected, server error, network) instead of a blanket
     * "could not be accepted".
     */
    uploadErrText(e) {
      const status = e?.response?.status;
      const body = e?.response?.data ?? e?.data;
      const reason = body?.report?.reason || body?.error || body?.message;
      if (reason) return reason;
      if (status === 413) {
        return "That PDF is too large for the server to accept (limit " + this.mb(this.maxUploadBytes) + ").";
      }
      if (status === 404) return "This design no longer exists. Reload the page and try again.";
      if (status === 422) return "That PDF was rejected. Re-export it as a standard, unencrypted print PDF.";
      if (status >= 500) return "The server could not process that upload (error " + status + "). Try again shortly.";
      if (!e?.response) return "The upload did not reach the server. Check your connection and try again.";
      return "That PDF could not be accepted (error " + status + ").";
    },
    mb(bytes) {
      return Math.round((bytes / (1024 * 1024)) * 10) / 10 + " MB";
    },
    async setOverlayPosition(key) {
      this.overlayPosition = key;
      if (!this.design.id) return;
      try {
        await this.api.patch("/api/v1/direct-mail/designs/" + this.design.id, {
          token_plan: { mode: "address_block", position: key },
        });
        this.toast("Address block position updated.");
      } catch (e) {
        /* non-fatal */
      }
    },
    toast(msg) {
      this.message = msg;
      if (this.messageTimer) clearTimeout(this.messageTimer);
      this.messageTimer = setTimeout(() => { this.message = ""; }, 3000);
    },
    errText(e, fallback) {
      const body = e?.response?.data ?? e?.data;
      return body?.message || body?.error || fallback;
    },
  },
};
</script>

<style scoped>
.dm-page { max-width: 1240px; }
.text-muted { color: var(--m-text-secondary); }
.dm-head-row { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.musubi-page__head h1 { font-size: 1.6rem; font-weight: 700; margin: 0 0 6px; display: flex; align-items: center; gap: 10px; }
.musubi-page__head p { margin: 0; max-width: 860px; line-height: 1.55; font-size: 0.92rem; }
.dm-badge { font-size: 0.6rem; font-weight: 800; letter-spacing: 0.05em; text-transform: uppercase; color: #fff; background: var(--m-accent, #4F46E5); border-radius: 999px; padding: 3px 8px; }
.plan-pill { display: inline-flex; align-items: center; gap: 5px; font-size: 0.6rem; font-weight: 800; letter-spacing: 0.05em; text-transform: uppercase; border-radius: 999px; padding: 3px 9px; }
.plan-pill--free { color: #6D28D9; background: #F3EEFE; border: 1px solid #E0D2FB; }

/* Stepper */
.dm-steps { display: flex; gap: 10px; margin: 18px 0 20px; flex-wrap: wrap; }
.dm-step { display: flex; align-items: center; gap: 10px; padding: 10px 16px; border: 1px solid var(--m-border); border-radius: 12px; background: var(--m-bg-card); flex: 1; min-width: 200px; }
.dm-step.is-current { border-color: var(--m-accent); box-shadow: 0 0 0 1px var(--m-accent) inset; }
.dm-step.is-todo { opacity: 0.6; }
.dm-step__num { width: 26px; height: 26px; border-radius: 50%; background: var(--m-accent-soft, #EEF0FF); color: var(--m-accent-hover, #4338CA); font-weight: 700; font-size: 0.82rem; display: flex; align-items: center; justify-content: center; flex: none; }
.dm-step.is-current .dm-step__num { background: var(--m-accent); color: #fff; }
.dm-step.is-done .dm-step__num { background: #DCF2ED; color: #0B6E63; }
.dm-step__title { display: block; font-weight: 600; font-size: 0.86rem; color: var(--m-text-primary); }
.dm-step__sub { display: block; font-size: 0.72rem; color: var(--m-text-tertiary); }

/* Cards */
.dm-card { background: var(--m-bg-card); border: 1px solid var(--m-border); border-radius: 14px; box-shadow: var(--m-shadow-card); margin-bottom: 18px; }
.dm-card__head { display: flex; align-items: center; gap: 9px; padding: 14px 16px; border-bottom: 1px solid var(--m-border); }
.dm-card__icon { color: var(--m-accent); }
.dm-card__title { font-size: 0.92rem; font-weight: 700; margin: 0; }
.dm-card__subtitle { font-size: 0.76rem; color: var(--m-text-tertiary); }
.dm-card__body { padding: 16px; }

/* Format grid */
.fmt-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 12px; }
.fmt-card { text-align: left; border: 1px solid var(--m-border); background: var(--m-bg-card); border-radius: 12px; padding: 13px; cursor: pointer; font-family: inherit; transition: border-color .15s, box-shadow .15s; }
.fmt-card:hover { border-color: var(--m-accent); }
.fmt-card.is-on { border-color: var(--m-accent); box-shadow: 0 0 0 1px var(--m-accent) inset; }
.fmt-card__vis { color: var(--m-accent); margin-bottom: 6px; }
.fmt-card__name { font-weight: 700; font-size: 0.86rem; color: var(--m-text-primary); }
.fmt-card__dim { font-size: 0.74rem; color: var(--m-text-secondary); margin-top: 2px; }
.fmt-card__cost { font-size: 0.72rem; color: var(--m-text-tertiary); margin-top: 6px; }

/* Two paths */
.dm-paths { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; align-items: start; }
@media (max-width: 980px) { .dm-paths { grid-template-columns: 1fr; } }
.path-head { display: flex; align-items: center; gap: 8px; color: var(--m-accent); }
.path-head h4 { margin: 0; font-size: 0.95rem; color: var(--m-text-primary); }
.path-sub { font-size: 0.8rem; color: var(--m-text-secondary); line-height: 1.5; margin: 6px 0 14px; }

/* Editor */
.editor { display: flex; flex-direction: column; gap: 14px; }
.toolbar { display: flex; flex-wrap: wrap; gap: 6px; }
.tool { display: inline-flex; align-items: center; gap: 5px; font-size: 0.76rem; font-weight: 600; padding: 5px 10px; border-radius: 8px; border: 1px solid var(--m-border); background: var(--m-bg-card); color: var(--m-text-secondary); cursor: default; }
.tool--ai { color: #6D28D9; border-color: #E0D2FB; background: #F7F3FE; cursor: not-allowed; font-family: inherit; }
.tool__lock { margin-left: 2px; }
.tool__soon { margin-left: 4px; font-size: 0.6rem; font-weight: 800; text-transform: uppercase; background: #E0D2FB; color: #6D28D9; border-radius: 999px; padding: 1px 6px; }
.lock-wrap { display: inline-flex; }
/* Front / Back side switch - same `is-on` pill treatment as .qr-size / .chip. */
.tool--sides { gap: 6px; padding: 4px 6px 4px 10px; }
.side-pill { font-size: 0.72rem; font-weight: 700; padding: 3px 10px; border-radius: 999px; border: 1px solid var(--m-border); background: var(--m-bg-card); color: var(--m-text-secondary); cursor: pointer; font-family: inherit; }
.side-pill:hover { border-color: var(--m-accent); }
.side-pill.is-on { background: var(--m-accent-soft, #EEF0FF); border-color: var(--m-accent); color: var(--m-accent-hover, #4338CA); }

.canvas { position: relative; border: 1px solid var(--m-border); border-radius: 10px; background: linear-gradient(180deg, #FBFCFE, var(--m-bg-card)); padding: 14px; display: flex; flex-direction: column; }
.canvas--back { justify-content: flex-end; }
.canvas__side { position: absolute; top: 8px; right: 10px; font-size: 0.64rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: var(--m-text-tertiary); }
.blk-edit { display: flex; flex-direction: column; gap: 8px; margin-top: 14px; }
.blk-input { border: 1px solid transparent; background: transparent; font-family: inherit; color: var(--m-text-primary); border-radius: 6px; padding: 5px 7px; width: 100%; resize: vertical; }
.blk-input:hover, .blk-input:focus { border-color: var(--m-border); background: var(--m-bg-card); outline: none; }
.blk-input--h { font-size: 1.15rem; font-weight: 700; color: var(--m-accent); }
.blk-input--p { font-size: 0.86rem; line-height: 1.45; }
.blk-input--cta { font-size: 0.82rem; font-weight: 700; }
.blk-img-row { display: flex; align-items: center; gap: 6px; color: var(--m-text-tertiary); font-size: 0.78rem; }
.blk-input--img { font-size: 0.78rem; }

/* QR block editor */
.blk-qr-row { display: flex; flex-direction: column; gap: 8px; margin-top: 4px; padding-top: 10px; border-top: 1px dashed var(--m-border); }
.qr-toggle { display: inline-flex; align-items: center; gap: 7px; font-size: 0.82rem; font-weight: 600; color: var(--m-text-primary); cursor: pointer; }
.qr-toggle svg { color: var(--m-accent); }
.qr-controls { display: flex; flex-wrap: wrap; align-items: center; gap: 14px; }
.qr-sizes { display: inline-flex; gap: 4px; }
.qr-size { font-size: 0.74rem; font-weight: 600; padding: 4px 10px; border-radius: 999px; border: 1px solid var(--m-border); background: var(--m-bg-card); color: var(--m-text-secondary); cursor: pointer; font-family: inherit; }
.qr-size:hover { border-color: var(--m-accent); }
.qr-size.is-on { background: var(--m-accent-soft, #EEF0FF); border-color: var(--m-accent); color: var(--m-accent-hover, #4338CA); }
.qr-cap-toggle { display: inline-flex; align-items: center; gap: 6px; font-size: 0.76rem; color: var(--m-text-secondary); cursor: pointer; }
.qr-sample { display: flex; align-items: center; gap: 11px; background: var(--m-accent-soft, #EEF0FF); border: 1px solid var(--m-border); border-radius: 10px; padding: 10px 12px; }
.qr-glyph { width: 40px; height: 40px; flex: none; display: flex; align-items: center; justify-content: center; border-radius: 8px; background: var(--m-bg-card); border: 1px solid var(--m-border); color: var(--m-accent); font-size: 1.1rem; }
.qr-sample-txt { font-size: 0.76rem; line-height: 1.45; color: var(--m-text-secondary); }
.qr-sample-txt b { color: var(--m-text-primary); font-weight: 700; }
.blk { margin: 0; }
.blk-addr { font-size: 0.9rem; line-height: 1.5; color: var(--m-text-primary); padding: 8px 7px; margin-top: 10px; border-left: 2px solid var(--m-accent-soft, #EEF0FF); }

.live-preview { margin-top: 12px; border-top: 1px dashed var(--m-border); padding-top: 10px; }
.live-preview__lbl { font-size: 0.64rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: var(--m-text-tertiary); margin-bottom: 6px; }
.lp-h { font-size: 1.1rem; font-weight: 700; color: var(--m-accent); line-height: 1.15; }
.lp-p { font-size: 0.85rem; line-height: 1.45; color: var(--m-text-primary); margin-top: 4px; }
.lp-cta { display: inline-block; margin-top: 8px; font-size: 0.78rem; font-weight: 700; color: #fff; background: var(--m-accent); padding: 5px 12px; border-radius: 6px; }

.f-row { display: flex; flex-direction: column; gap: 6px; }
.f-lbl { font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--m-text-secondary); }
.f-note { font-size: 0.72rem; color: var(--m-text-tertiary); display: inline-flex; align-items: center; gap: 5px; }
.token-palette { display: flex; flex-wrap: wrap; gap: 6px; }
.token { font-family: ui-monospace, monospace; font-size: 0.72rem; border: 1px solid var(--m-border); background: var(--m-bg-card); color: var(--m-text-secondary); border-radius: 6px; padding: 4px 8px; cursor: pointer; }
.token:hover { border-color: var(--m-accent); color: var(--m-accent-hover, #4338CA); }
.token.is-known { color: #0B6E63; border-color: #B6EBE4; background: #ECFBF8; }

.info-state { display: flex; gap: 9px; align-items: flex-start; font-size: 0.78rem; line-height: 1.45; color: var(--m-text-secondary); background: var(--m-accent-soft, #EEF0FF); border: 1px solid var(--m-border); border-radius: 10px; padding: 11px 13px; }
.info-state > svg { flex: none; margin-top: 1px; color: var(--m-accent); }
.info-state--violet { background: #F7F3FE; border-color: #E0D2FB; color: #4C1D95; }
.info-state--violet > svg { color: #7C3AED; }
.violet-link { color: #6D28D9; font-weight: 700; }

/* Dropzone */
.dropzone { display: block; text-align: center; border: 1.5px dashed var(--m-border); border-radius: 12px; padding: 22px 14px; cursor: pointer; position: relative; }
.dropzone:hover { border-color: var(--m-accent); }
.dropzone.is-busy { opacity: 0.7; }
.dropzone__input { position: absolute; inset: 0; opacity: 0; cursor: pointer; }
.dropzone__ic { color: var(--m-accent); margin-bottom: 6px; }
.dropzone .hint { font-size: 0.72rem; color: var(--m-text-tertiary); margin-top: 4px; }

.scan { border: 1px solid var(--m-border); border-radius: 10px; margin-top: 14px; overflow: hidden; }
.scan__head { display: flex; align-items: center; gap: 6px; font-size: 0.78rem; font-weight: 700; color: #0B6E63; background: #ECFBF8; padding: 9px 12px; }
.scan__body { padding: 10px 12px; display: flex; flex-direction: column; gap: 6px; }
.scan-token { display: flex; justify-content: space-between; font-size: 0.74rem; }
.scan-token .tkn { font-family: ui-monospace, monospace; color: var(--m-text-primary); }
.scan-token .pg { color: var(--m-text-tertiary); }

.validation { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 12px; }
.vchip { display: inline-flex; align-items: center; gap: 5px; font-size: 0.72rem; font-weight: 600; padding: 4px 9px; border-radius: 999px; }
.vchip.ok { color: #0B6E63; background: #ECFBF8; border: 1px solid #B6EBE4; }
.vchip.warn { color: #9A5B12; background: #FFF7EA; border: 1px solid #F4D9A8; }
.vchip.note { color: var(--m-text-secondary); background: var(--m-bg-page, #F4F5F9); border: 1px solid var(--m-border); }

.chip-row { display: flex; flex-wrap: wrap; gap: 6px; }
.chip { display: inline-flex; align-items: center; gap: 5px; font-size: 0.78rem; font-weight: 600; padding: 5px 11px; border-radius: 999px; border: 1px solid var(--m-border); background: var(--m-bg-card); color: var(--m-text-secondary); cursor: pointer; }
.chip.is-on { background: var(--m-accent-soft, #EEF0FF); border-color: var(--m-accent); color: var(--m-accent-hover, #4338CA); }

/* Action bar */
.dm-action-bar { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.dm-action-bar__note { font-size: 0.78rem; color: var(--m-text-tertiary); }
.dm-action-bar__spacer { flex: 1; }

/* Buttons */
.btn { font-family: inherit; font-weight: 600; font-size: 0.82rem; padding: 0.5rem 0.95rem; border-radius: 8px; border: 1px solid transparent; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; text-decoration: none; line-height: 1.3; white-space: nowrap; }
.btn:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-pri { background: var(--m-accent); border-color: var(--m-accent); color: #fff; }
.btn-pri:hover:not(:disabled) { background: var(--m-accent-hover); }
.btn-sec { color: var(--m-text-secondary); border-color: var(--m-border); background: var(--m-bg-card); }
.btn-sec:hover:not(:disabled) { background: var(--m-accent-soft); border-color: var(--m-accent); color: var(--m-text-primary); }

/* Alert / toast */
.dm-alert { padding: 9px 12px; font-size: 0.8rem; border-radius: 8px; }
.dm-alert--err { background: #FCECEC; color: #B42318; border: 1px solid #F5C9C4; }
.dm-toast { position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%); background: #0B6E63; color: #fff; padding: 10px 18px; border-radius: 999px; font-size: 0.84rem; font-weight: 600; display: flex; align-items: center; gap: 8px; box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2); z-index: 1100; }
.dm-toast-enter-active, .dm-toast-leave-active { transition: opacity 0.25s, transform 0.25s; }
.dm-toast-enter-from, .dm-toast-leave-to { opacity: 0; transform: translateX(-50%) translateY(10px); }
</style>
